/**
 * Pinia Store: Newsletter Subscription Topic
 *
 * Migrated from Vuex: src/extensions/newsletter/store/modules/subscriptionTopic.js
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import SubscriptionTopicApi from '@/extensions/newsletter/store/api/subscriptionTopic';
import { useSetupStore } from '@/stores';

const form = {
    name: '',
    description: '',
    force: false,
    hidden: false,
};

const rules = {
    name: 'required',
    // key: 'required|unique_newsletter_subscriptionTopic_key'
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false,
    }
};

export const useNewsletterSubscriptionTopicStore = defineStore('newsletterSubscriptionTopic', {
    state: () => ({
        loading: true,
        subscriptionTopicsPaginated: {
            path: null,
            variables: {
                order: 'created_at',
                direction: 'desc',
                // name: '',
                // categories: [],
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            // category: {
            //     loading: false,
            //     items: []
            // },
            // extensions: {
            //     shop: {
            //         category: {
            //             loading: false,
            //             items: []
            //         }
            //     }
            // }
        },
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // Mutations converted to actions
        addPaginatedSubscriptionTopicsToState(data) {
            this.subscriptionTopicsPaginated.items = data.items;
            this.subscriptionTopicsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.subscriptionTopicsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.subscriptionTopicsPaginated.link = _.cloneDeep(data.config.link);
            this.subscriptionTopicsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.subscriptionTopicsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.subscriptionTopicsPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.subscriptionTopicsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.subscriptionTopicsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.subscriptionTopicsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.subscriptionTopicsPaginated.filters, key);
            } else {
                Vue.set(this.subscriptionTopicsPaginated.filters, key, value);
            }
        },

        definePathValue(value) {
            this.subscriptionTopicsPaginated.path = value;
        },

        defineSorting({ key, value }) {
            this.subscriptionTopicsPaginated.variables.order = key;
            this.subscriptionTopicsPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.subscriptionTopicsPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(newRules) {
            this.rules = newRules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui.form.initialized = false;
            this.ui.form.validateOnEnter = false;
        },

        insertSubscriptionTopic(subscriptionTopic) {
            this.form = subscriptionTopic;
        },

        // Form field setters
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        setFormName(value) {
            this.form.name = value;
        },

        setFormDescription(value) {
            this.form.description = value;
        },

        setFormForce(value) {
            this.form.force = !!value;
        },

        setFormHidden(value) {
            this.form.hidden = !!value;
        },

        // Async actions
        fetchSubscriptionTopicsPaginated() {
            return SubscriptionTopicApi.fetchSubscriptionTopicsPaginated(this.subscriptionTopicsPaginated).then(data => {
                this.addPaginatedSubscriptionTopicsToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchSubscriptionTopic(id) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (cms) {
                return SubscriptionTopicApi.fetchSubscriptionTopic(id, cms.id).then(subscriptionTopic => {
                    this.insertSubscriptionTopic(_.cloneDeep(subscriptionTopic));
                    this.resetRules();
                });
            }
        },

        initializeSubscriptionTopic(key) {
            this.resetRules();
        },

        create(subscriptionTopic) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (cms) {
                subscriptionTopic.cms = { id: cms.id };
                return SubscriptionTopicApi.create(subscriptionTopic).then(subscriptionTopic => {
                    this.insertSubscriptionTopic(_.cloneDeep(subscriptionTopic));
                    this.resetRules();
                });
            }
        },

        update(subscriptionTopic) {
            return SubscriptionTopicApi.update(subscriptionTopic).then(subscriptionTopic => {
                this.insertSubscriptionTopic(_.cloneDeep(subscriptionTopic));
                this.resetRules();
            });
        },

        deleteSubscriptionTopic(id) {
            return SubscriptionTopicApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertSubscriptionTopic(_.cloneDeep(form));
        }
    }
});
